import { create } from "zustand";
import type { components } from "@cntrl/api-client";
import { useMe } from "./hooks/useSession.js";

type MeDocument = components["schemas"]["MeDocument"];

/**
 * The client's ACTIVE band selection. There is no `POST /switch-band` (the MVP's
 * version minted a session for a different user id; gone with the person/tenant split). The client holds the
 * selection and re-fetches /me/navigation?context=band:{id}. Deliberately NOT registered with the
 * store-reset registry: switchBand() resets feature stores then sets THIS to the new band, so it must
 * survive the scorch (it is the new tenant id, not stale tenant data).
 *
 * <b>The selection is remembered on the DEVICE, in a SESSION cookie (ART-319).</b> It used to live in memory
 * only, so any reload — ⌘R on the web, and on iOS the live-update reload that lands on the next page change —
 * dropped the person back on the first band in their list. A cookie with no expiry survives a reload and
 * dies when the browser or app is closed, which is exactly the split the product wants: a reload keeps you
 * where you were, a fresh open starts on your STARRED band (`MeDocument.starredBandId`, on the account so
 * every device shares it). The device half is deliberately not server state — two devices can be on two
 * different bands. A cookie rather than storage because localStorage/sessionStorage are lint-banned (the
 * `theme.ts` precedent). Not auth material: a band id grants nothing, and every read is still gated server-side.
 */
interface ActiveBandState {
  activeBandId: string | null;
  setActiveBand: (bandId: string) => void;
  clear: () => void;
}

const COOKIE_NAME = "cntrl_band";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readRemembered(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)cntrl_band=([^;]*)/);
  const value = match?.[1] ? decodeURIComponent(match[1]) : null;
  return value && UUID.test(value) ? value : null;
}

function writeRemembered(bandId: string | null): void {
  if (typeof document === "undefined") return;
  // No max-age/expires ⇒ a SESSION cookie: kept across reloads, gone when the browser/app closes.
  document.cookie = bandId
    ? `${COOKIE_NAME}=${encodeURIComponent(bandId)}; path=/; samesite=lax`
    : `${COOKIE_NAME}=; path=/; max-age=0; samesite=lax`;
}

export const useActiveBand = create<ActiveBandState>((set) => ({
  activeBandId: readRemembered(),
  setActiveBand: (bandId) => {
    writeRemembered(bandId);
    set({ activeBandId: bandId });
  },
  clear: () => {
    writeRemembered(null);
    set({ activeBandId: null });
  },
}));

/**
 * Which band the shell opens, given the person and the device's remembered selection. In order: the band
 * this device was last on, then the account's starred band, then the server's hint, then the first band seat.
 * Every candidate must still match a band seat — a remembered or starred band the person has since left (or
 * one remembered for a different person on a shared device) falls through rather than asking for a
 * navigation document they cannot reach. Nothing resolves until `/me` has loaded, for the same reason.
 */
export function resolveActiveBandId(me: MeDocument | undefined, remembered: string | null): string | undefined {
  if (!me) return undefined;
  const bandIds = (me.seats ?? []).filter((s) => s.kind === "band" && s.bandId).map((s) => s.bandId as string);
  const held = (id: string | null | undefined): id is string => !!id && bandIds.includes(id);
  if (held(remembered)) return remembered;
  if (held(me.starredBandId)) return me.starredBandId;
  if (held(me.activeBandId)) return me.activeBandId;
  return bandIds[0];
}

/** The resolved active band for the signed-in person — see {@link resolveActiveBandId}. */
export function useResolvedActiveBandId(): string | undefined {
  const me = useMe();
  const remembered = useActiveBand((s) => s.activeBandId);
  return resolveActiveBandId(me.data, remembered);
}
